import { useCentralStore } from "@/dentlab/src/store/Central";
import { useAttributesStore } from "@/dentlab/src/store/Attributes/attributes.store";
import { AppBarAppsListProps } from "../../app-bar-types";
import { PageSidebarItemWithChatPreview } from "../PageSidebarItemWithChatPreview";
import { ORG_SUBPAGE_FAVORITE_MAP } from "../sidebar-favorites-dialog.component";
import {
	ChevronRight,
	ChevronDown,
	MessageCircleQuestion,
	Settings,
} from "lucide-react";

import { cn } from "@/dentlab/src/lib/utils/cn";
import { useEffect, useMemo, useState } from "react";
import { getAppListMenuItems } from "./get-app-list-menu-items.function";
import { RolesEnum } from "@/lib/types/roles.types";
import { useUploadGuard } from "@/dentlab/src/hooks/useUploadGuard";
import { hasFullFeatureSet } from "@/dentlab/src/store/Central/selectors";
import { FeatureGateModal } from "@/dentlab/src/components/subscription/feature-gate-modal.component";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";
import { useRemindersStore } from "@/dentlab/src/store/Reminders";

const STORAGE_KEY = "dentlab-sidebar-apps-collapsed";

export const AppBarAppsList: React.FC<AppBarAppsListProps> = ({
	expanded,
	excludeKeys,
	hiddenKeys,
	suppressedKeys,
	onChatInteraction,
	onAppInteraction,
	onGearClick,
}) => {
	const role = useCentralStore((state) => state.role ?? 2);
	const subscriptionTier = useCentralStore((state) => state.subscriptionTier);
	const showReminderBadge = useCentralStore(
		(state) =>
			!!state.organization?.show_reminder_sidebar_badge &&
			(state.role ?? RolesEnum.MEMBER) <= RolesEnum.ADMIN
	);
	const dueReminderCount = useRemindersStore((state) => state.dueCount);

	useEffect(() => {
		if (showReminderBadge) useRemindersStore.getState().fetchDueCount();
	}, [showReminderBadge]);
	const checkNavigationAllowed = useUploadGuard();
	const [intercomOpen, setIntercomOpen] = useState(false);
	const [showGateModal, setShowGateModal] = useState(false);
	const [isCollapsed, setIsCollapsed] = useState(() =>
		getLocalStorage(STORAGE_KEY, false)
	);

	const handleToggleCollapse = () => {
		const newValue = !isCollapsed;
		setIsCollapsed(newValue);
		setLocalStorage(STORAGE_KEY, newValue);
	};

	const toggleIntercom = () => {
		if (!window.Intercom) return;
		if (intercomOpen) {
			window.Intercom("hide");
		} else {
			window.Intercom("show");
		}
		setIntercomOpen(!intercomOpen);
	};

	const attributes = useAttributesStore((state) => state.attributes);
	const menuItems = useMemo(() => getAppListMenuItems(role), [role]);

	const visibleOrgItems = useMemo(() => {
		if (role > RolesEnum.ADMIN) return [];
		return Object.entries(ORG_SUBPAGE_FAVORITE_MAP)
			.filter(
				([key]) =>
					attributes[`sidebar-fav-${key}`] !== 1 &&
					attributes[`sidebar-show-${key}`] === 1
			)
			.map(([key, item]) => ({
				key,
				title: item.title,
				path: item.path,
				icon: item.icon,
				isGated: false,
			}));
	}, [role, attributes]);

	const filteredItems = useMemo(
		() => [
			...menuItems.filter(
				(item) =>
					!excludeKeys?.includes(item.key) &&
					!hiddenKeys?.includes(item.key)
			),
			...visibleOrgItems,
		],
		[menuItems, excludeKeys, hiddenKeys, visibleOrgItems]
	);

	return (
		<div className="flex flex-col w-full">
			{expanded ? (
				<button
					onClick={handleToggleCollapse}
					className="flex-shrink-0 flex items-center w-full px-2 py-1 text-sm font-medium text-gray-700 hover:bg-stone-200 rounded-md transition-colors"
					aria-expanded={!isCollapsed}
					aria-label="Navigation ein-/ausklappen"
					data-testid="apps-list-toggle"
				>
					{isCollapsed ? (
						<ChevronRight size={20} className="flex-shrink-0" />
					) : (
						<ChevronDown size={20} className="flex-shrink-0" />
					)}
					<span className="ml-1 flex-1 text-left">Navigation</span>
					<div
						onClick={(e) => {
							e.stopPropagation();
							onGearClick?.();
						}}
						className="flex-shrink-0 p-0.5 rounded hover:bg-stone-300 transition-colors"
						aria-label="Navigation anpassen"
						data-testid="sidebar-favorites-gear"
					>
						<Settings size={14} />
					</div>
				</button>
			) : (
				<div
					onClick={handleToggleCollapse}
					className="flex justify-center py-1 rounded-md cursor-pointer transition-colors duration-200 text-gray-700 hover:bg-stone-200"
					data-testid="apps-list-toggle"
					aria-label="Navigation ein-/ausklappen"
				>
					<Settings size={18} />
				</div>
			)}

			{!isCollapsed && filteredItems.length > 0 && (
				<div className="flex flex-col gap-0.5">
					{filteredItems.map((item) => (
						<PageSidebarItemWithChatPreview
							key={item.key}
							itemKey={item.key}
							title={item.title}
							path={item.path}
							icon={item.icon}
							expanded={expanded}
							onChatInteraction={
								item.key === "chats"
									? onChatInteraction
									: undefined
							}
							onAppInteraction={onAppInteraction}
							onBeforeNavigate={checkNavigationAllowed}
							isLocked={
								item.isGated &&
								!hasFullFeatureSet(subscriptionTier)
							}
							onLockedClick={() => setShowGateModal(true)}
							badgeCount={
								item.key === "debitoren" && showReminderBadge
									? dueReminderCount
									: 0
							}
							badgeLabel={`${dueReminderCount} fällige Mahnungen`}
							suppressHighlight={suppressedKeys?.has(item.key)}
						/>
					))}
				</div>
			)}
			<FeatureGateModal
				open={showGateModal}
				onOpenChange={setShowGateModal}
			/>

			<div
				onClick={toggleIntercom}
				className={cn(
					"flex items-center gap-2 pl-2 py-1 rounded-md cursor-pointer transition-colors duration-200 text-gray-700 hover:bg-stone-200",
					expanded ? "justify-start pl-2" : "justify-center px-0"
				)}
			>
				<div className="w-6 h-6 flex items-center justify-center">
					<MessageCircleQuestion size={16} color="rgb(55, 65, 81)" />
				</div>
				{expanded && (
					<span className="text-sm font-medium truncate">
						Support
					</span>
				)}
			</div>
		</div>
	);
};
